import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Save, Palette, Wand2, Sparkles } from 'lucide-react';
import type { PluginManifest } from '@/types';

interface PluginSettingsProps {
  manifest: PluginManifest;
  config: Record<string, any>;
  projectId: string;
  pluginId: string;
}

/**
 * Thridify: the experience-switcher curation panel. The setting LIVES IN
 * THRIDIFY (team-level, published inside the account's experiences.json);
 * this panel is the Tyashin entry point to the same field the Thridify
 * admin edits. GET returns the published experience list so the picker
 * shows real labels.
 */

/**
 * Thridify enterprise sandbox: which environment the STOREFRONT serves.
 * Only accounts Thridify marked with sandbox access see anything beyond the
 * production badge; the backend enforces the entitlement on the link and on
 * every toggle regardless of what this panel shows.
 */
function ThridifyEnvironmentPanel({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['thridify-environment', projectId],
    queryFn: () => apiHelpers.thridifyGetEnvironment(projectId),
    retry: 1,
  });
  const payload = (data as any)?.data?.data as
    | {
        environment: 'production' | 'sandbox';
        sandboxAccess: boolean;
        prodLinked: boolean;
        prodLinkedDomain: string | null;
        prodAccountEmail: string | null;
        prodAccountName: string | null;
        sandboxLinked: boolean;
        sandboxLinkedDomain: string | null;
        sandboxAccountEmail: string | null;
        sandboxAccountName: string | null;
      }
    | undefined;

  const setEnvMutation = useMutation({
    mutationFn: (environment: 'production' | 'sandbox') =>
      apiHelpers.thridifySetEnvironment(projectId, environment),
    onSuccess: (_res, environment) => {
      queryClient.invalidateQueries({ queryKey: ['thridify-environment', projectId] });
      toast.success(
        environment === 'sandbox'
          ? 'Your website now serves SANDBOX 3D content. Flip back to Production when done testing.'
          : 'Your website now serves Production 3D content.'
      );
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to switch environment'),
  });

  const linkMutation = useMutation({
    mutationFn: () => apiHelpers.thridifyStartSandboxLink(projectId),
    onSuccess: (res) => {
      const url = (res as any)?.data?.data?.redirectUrl;
      if (url) window.location.href = url;
      else toast.error('No redirect URL returned');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to start sandbox link'),
  });

  const disconnectMutation = useMutation({
    mutationFn: (environment: 'production' | 'sandbox') =>
      apiHelpers.thridifyDisconnect(projectId, environment),
    onSuccess: (res, environment) => {
      queryClient.invalidateQueries({ queryKey: ['thridify-environment', projectId] });
      queryClient.invalidateQueries({ queryKey: ['thridify-switcher', projectId] });
      const message = (res as any)?.data?.data?.message;
      toast.success(
        message ||
          (environment === 'sandbox'
            ? 'Sandbox account disconnected.'
            : 'Thridify account disconnected — 3D is off until you reconnect.')
      );
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to disconnect'),
  });

  const handleDisconnect = (environment: 'production' | 'sandbox') => {
    const message =
      environment === 'sandbox'
        ? 'Disconnect the sandbox account? Your website falls back to Production content, and the sandbox link is revoked on Thridify.'
        : 'Log out of your Thridify account? 3D experiences stop appearing on your website until you reconnect. The link is revoked on Thridify.';
    if (window.confirm(message)) {
      disconnectMutation.mutate(environment);
    }
  };

  if (isLoading || !payload) return null;
  // Non-enterprise accounts: no sandbox surface at all (not even a badge row
  // that begs questions) unless they somehow already linked one.
  if (!payload.sandboxAccess && !payload.sandboxLinked) return null;

  const isSandboxLive = payload.environment === 'sandbox';

  const handleToggle = (environment: 'production' | 'sandbox') => {
    if (environment === payload.environment) return;
    const message =
      environment === 'sandbox'
        ? 'Serve SANDBOX (dev) 3D content on your LIVE website? Visitors will see experiences from your sandbox account until you switch back.'
        : 'Switch your website back to Production 3D content?';
    if (window.confirm(message)) {
      setEnvMutation.mutate(environment);
    }
  };

  return (
    <div
      className={`space-y-3 rounded-xl border p-4 ${isSandboxLive ? 'border-amber-500/60 bg-amber-500/5' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <h4 className="flex items-center gap-2 text-sm font-medium">
            3D environment
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                isSandboxLive ? 'bg-amber-500 text-black' : 'bg-emerald-600 text-white'
              }`}
            >
              {isSandboxLive ? 'Sandbox live' : 'Production'}
            </span>
          </h4>
          <p className="text-muted-foreground text-xs">
            Enterprise sandbox: build and verify experiences in Thridify&apos;s dev environment,
            preview them on your live website, then switch to Production at launch.
            {payload.sandboxLinkedDomain
              ? ` Sandbox linked for ${payload.sandboxLinkedDomain}.`
              : ''}
          </p>
          {payload.sandboxLinked && (payload.sandboxAccountEmail || payload.sandboxAccountName) ? (
            <p className="text-muted-foreground/70 mt-1 break-all text-[11px]">
              Sandbox account: {payload.sandboxAccountEmail || payload.sandboxAccountName}
            </p>
          ) : null}
        </div>
      </div>
      {!payload.sandboxLinked ? (
        <button
          onClick={() => linkMutation.mutate()}
          disabled={linkMutation.isPending}
          className="border-primary text-primary hover:bg-primary hover:text-primary-foreground flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
        >
          {linkMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Wand2 className="h-4 w-4" />
          )}
          Link sandbox account
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleToggle('production')}
            disabled={setEnvMutation.isPending || !isSandboxLive}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
              !isSandboxLive ? 'border-emerald-600 bg-emerald-600 text-white' : 'hover:bg-muted'
            }`}
          >
            Production
          </button>
          <button
            onClick={() => handleToggle('sandbox')}
            disabled={setEnvMutation.isPending || isSandboxLive}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
              isSandboxLive ? 'border-amber-500 bg-amber-500 text-black' : 'hover:bg-muted'
            }`}
          >
            Sandbox
          </button>
          {setEnvMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          <button
            onClick={() => handleDisconnect('sandbox')}
            disabled={disconnectMutation.isPending}
            className="text-muted-foreground hover:text-destructive ml-auto rounded-lg border border-dashed px-3 py-2 text-xs font-medium transition-colors"
          >
            Disconnect sandbox
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Thridify account connection ("log in / log out"): shown for EVERY project
 * with the plugin installed, sandbox or not. Disconnecting revokes the link
 * on Thridify and stops 3D on the website until reconnected — an explicit
 * merchant right, independent of uninstalling.
 */
function ThridifyConnectionPanel({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['thridify-environment', projectId],
    queryFn: () => apiHelpers.thridifyGetEnvironment(projectId),
    retry: 1,
  });
  const payload = (data as any)?.data?.data as
    | {
        prodLinked: boolean;
        prodLinkedDomain: string | null;
        prodAccountEmail: string | null;
        prodAccountName: string | null;
      }
    | undefined;

  const connectMutation = useMutation({
    mutationFn: () => apiHelpers.thridifyStartLink(projectId, 'production'),
    onSuccess: (res) => {
      const url = (res as any)?.data?.data?.redirectUrl;
      if (url) window.location.href = url;
      else toast.error('No redirect URL returned');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to start the Thridify login'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiHelpers.thridifyDisconnect(projectId, 'production'),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['thridify-environment', projectId] });
      queryClient.invalidateQueries({ queryKey: ['thridify-switcher', projectId] });
      toast.success(
        (res as any)?.data?.data?.message ||
          'Thridify account disconnected — 3D is off until you reconnect.'
      );
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to disconnect'),
  });

  if (isLoading || !payload) return null;

  return (
    <div className="bg-muted/30 flex items-center justify-between rounded-xl border p-4">
      <div>
        <h4 className="text-sm font-medium">Thridify account</h4>
        <p className="text-muted-foreground text-xs">
          {payload.prodLinked
            ? 'Connected. Disconnecting logs your website out of Thridify: 3D experiences stop appearing until you reconnect.'
            : 'Not connected — log in with your Thridify account to show 3D experiences on your website.'}
        </p>
        {payload.prodLinked &&
        (payload.prodAccountEmail || payload.prodAccountName || payload.prodLinkedDomain) ? (
          <p className="text-muted-foreground/70 mt-1 break-all text-[11px]">
            {payload.prodAccountEmail || payload.prodAccountName || 'Connected'}
            {payload.prodLinkedDomain ? ` · ${payload.prodLinkedDomain}` : ''}
          </p>
        ) : null}
      </div>
      {payload.prodLinked ? (
        <button
          onClick={() => {
            if (
              window.confirm(
                'Log out of your Thridify account? 3D experiences stop appearing on your website until you reconnect. The link is revoked on Thridify.'
              )
            ) {
              disconnectMutation.mutate();
            }
          }}
          disabled={disconnectMutation.isPending}
          className="text-destructive border-destructive/40 hover:bg-destructive hover:text-destructive-foreground flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
        >
          {disconnectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Disconnect
        </button>
      ) : (
        <button
          onClick={() => connectMutation.mutate()}
          disabled={connectMutation.isPending}
          className="border-primary text-primary hover:bg-primary hover:text-primary-foreground flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
        >
          {connectMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Wand2 className="h-4 w-4" />
          )}
          Connect Thridify account
        </button>
      )}
    </div>
  );
}

/**
 * Live placement preview — a mock product page that re-renders as the
 * Display settings change, so "top-right" and "after the CTA" mean something
 * concrete before saving. Purely visual: mirrors the floating-icon bundle's
 * placement rules (icon corners are relative to the GALLERY, flow buttons
 * insert after the CTA / description slots).
 */
function ThridifyPlacementPreview({ formState }: { formState: Record<string, any> }) {
  const presentation = String(formState.presentation ?? 'replace-gallery');
  const mode = presentation === 'button' ? 'button' : presentation === 'icon' ? 'icon' : 'inline';
  const posClass =
    (
      {
        'top-right': 'top-2 right-2',
        'top-left': 'top-2 left-2',
        'bottom-right': 'bottom-2 right-2',
        'bottom-left': 'bottom-2 left-2',
      } as Record<string, string>
    )[String(formState.triggerPosition)] ?? 'top-2 right-2';
  const placement = ['after-cta', 'after-description', 'gallery'].includes(
    String(formState.buttonPlacement)
  )
    ? String(formState.buttonPlacement)
    : 'after-cta';
  const buttonText = String(formState.buttonText || 'View in 3D').slice(0, 40);

  const triggerButton = (
    <span className="border-primary text-primary bg-background inline-flex w-fit items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium">
      <span className="bg-primary/15 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[7px]">
        3D
      </span>
      {buttonText}
    </span>
  );
  const iconBadge = (
    <span
      className={`bg-background/90 border-primary text-primary absolute ${posClass} flex h-7 w-7 items-center justify-center rounded-full border text-[9px] font-semibold shadow-sm`}
    >
      3D
    </span>
  );

  return (
    <div className="rounded-xl border p-4">
      <h4 className="mb-1 text-sm font-medium">Placement preview</h4>
      <p className="text-muted-foreground mb-3 text-xs">
        How the 3D trigger will sit on a product page with these settings. Updates as you edit —
        save to apply.
      </p>
      <div className="bg-muted/30 grid grid-cols-2 gap-4 rounded-lg border p-4 text-xs">
        {/* Gallery column */}
        <div>
          <div className="bg-muted relative flex aspect-square items-center justify-center rounded-md border">
            <span className="text-muted-foreground">Product gallery</span>
            {mode === 'icon' && iconBadge}
            {mode === 'button' && placement === 'gallery' && (
              <span className={`absolute ${posClass}`}>{triggerButton}</span>
            )}
            {mode === 'inline' && (
              <span className="bg-background/80 text-muted-foreground absolute inset-0 flex items-center justify-center rounded-md text-[11px]">
                3D experience replaces gallery
              </span>
            )}
          </div>
          {mode === 'inline' && (
            <div className="mt-1.5 flex gap-1">
              <span className="bg-background rounded border px-2 py-0.5 text-[10px]">Images</span>
              <span className="border-primary text-primary bg-background rounded border px-2 py-0.5 text-[10px]">
                3D View
              </span>
            </div>
          )}
        </div>
        {/* Details column */}
        <div className="flex flex-col gap-2">
          <div className="bg-muted-foreground/30 h-3 w-2/3 rounded" />
          <div className="bg-muted-foreground/20 h-2 w-1/3 rounded" />
          <div className="bg-primary text-primary-foreground mt-1 w-fit rounded-md px-3 py-1 text-[11px]">
            Enquire / Buy
          </div>
          {mode === 'button' && placement === 'after-cta' && triggerButton}
          <div className="mt-1 space-y-1">
            <div className="bg-muted-foreground/15 h-2 w-full rounded" />
            <div className="bg-muted-foreground/15 h-2 w-5/6 rounded" />
            <div className="bg-muted-foreground/15 h-2 w-4/6 rounded" />
          </div>
          {mode === 'button' && placement === 'after-description' && triggerButton}
        </div>
      </div>
    </div>
  );
}

function ThridifySwitcherPanel({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['thridify-switcher', projectId],
    queryFn: () => apiHelpers.thridifyGetSwitcher(projectId),
    retry: 1,
  });
  const payload = (data as any)?.data?.data as
    | {
        switcher: { enabled?: boolean; presentOn?: string[] } | null;
        experiences: Array<{ pid: string; label: string }>;
      }
    | undefined;

  const [draft, setDraft] = useState<{ enabled: boolean; presentOn: string[] } | null>(null);
  const current = draft ?? {
    enabled: payload?.switcher?.enabled !== false,
    presentOn: payload?.switcher?.presentOn ?? [],
  };
  const experiences = payload?.experiences ?? [];

  const saveMutation = useMutation({
    mutationFn: () =>
      apiHelpers.thridifySaveSwitcher(
        projectId,
        current.enabled && current.presentOn.length === 0
          ? null // default: switcher on, whole catalogue
          : { enabled: current.enabled, presentOn: current.presentOn }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thridify-switcher', projectId] });
      toast.success('Switcher settings saved — live on your site within a minute.');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to save switcher settings'),
  });

  if (isLoading) {
    return (
      <div className="bg-muted/50 rounded-xl border border-dashed p-4 text-sm">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading experience switcher settings…
      </div>
    );
  }
  if (isError || !payload) {
    return (
      <div className="bg-muted/50 text-muted-foreground rounded-xl border border-dashed p-4 text-xs">
        Experience-switcher settings are unavailable — link your Thridify account first (use
        “Refresh Thridify link” above).
      </div>
    );
  }

  const togglePin = (pid: string) => {
    setDraft({
      enabled: current.enabled,
      presentOn: current.presentOn.includes(pid)
        ? current.presentOn.filter((p) => p !== pid)
        : [...current.presentOn, pid],
    });
  };

  return (
    <div className="space-y-3 rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">3D experience switcher</h4>
          <p className="text-muted-foreground text-xs">
            Shoppers viewing a 3D finish can move it between your published experiences (door, wall,
            corridor…). Choose which experiences appear in that tray — none selected means all of
            them, in publish order. Saved to your Thridify account.
          </p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={current.enabled}
            onChange={(e) => setDraft({ ...current, enabled: e.target.checked })}
          />
          Enabled
        </label>
      </div>
      {current.enabled && experiences.length > 0 && (
        <div className="grid max-h-48 grid-cols-1 gap-1 overflow-auto sm:grid-cols-2">
          {experiences.map((exp) => (
            <label key={exp.pid} className="flex items-center gap-2 rounded px-2 py-1 text-sm">
              <input
                type="checkbox"
                checked={current.presentOn.includes(exp.pid)}
                onChange={() => togglePin(exp.pid)}
              />
              <span className="truncate">{exp.label}</span>
            </label>
          ))}
        </div>
      )}
      <button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending || !draft}
        className="border-primary text-primary hover:bg-primary hover:text-primary-foreground flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
      >
        {saveMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        Save switcher settings
      </button>
    </div>
  );
}

export function PluginSettings({ manifest, config, projectId, pluginId }: PluginSettingsProps) {
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState<Record<string, any>>({ ...config });

  const saveMutation = useMutation({
    mutationFn: () => apiHelpers.updatePluginConfig(projectId, pluginId, { config: formState }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugin-manifest', projectId, pluginId] });
      toast.success('Settings saved');
    },
    onError: () => toast.error('Failed to save settings'),
  });

  const syncBrandKitMutation = useMutation({
    mutationFn: () => apiHelpers.syncPluginBrandKit(projectId, pluginId),
    onSuccess: (res) => {
      const applied = res.data?.data?.applied;
      if (applied) {
        setFormState((prev) => ({ ...prev, ...applied }));
      }
      queryClient.invalidateQueries({ queryKey: ['plugin-manifest', projectId, pluginId] });
      toast.success('Theme synced from brand kit');
    },
    onError: () => toast.error('Failed to sync brand kit'),
  });

  // SEO Co-Pilot: pull business info from project + live-site data (fill-only).
  const resyncMutation = useMutation({
    mutationFn: () => apiHelpers.seoCopilotResync(projectId),
    onSuccess: (res) => {
      const applied = (res as any)?.data?.data?.applied || {};
      const count = Object.keys(applied).length;
      if (count > 0) setFormState((prev) => ({ ...prev, ...applied }));
      queryClient.invalidateQueries({ queryKey: ['plugin-manifest', projectId, pluginId] });
      toast.success(
        count > 0
          ? `Imported ${count} field${count === 1 ? '' : 's'} from your site & project data.`
          : 'Everything is already filled in — nothing to import.'
      );
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to import data'),
  });

  const thridifyResyncMutation = useMutation({
    mutationFn: () => apiHelpers.thridifyResync(projectId),
    onSuccess: (res) => {
      const data = (res as any)?.data?.data || {};
      queryClient.invalidateQueries({ queryKey: ['plugin-manifest', projectId, pluginId] });
      const pushNote =
        data.schemaOk === false || data.catalogOk === false
          ? ' Catalog push had errors — check again shortly.'
          : '';
      toast.success(
        `Thridify link refreshed${data.domain ? ` for ${data.domain}` : ''}.${pushNote}`
      );
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Failed to refresh the Thridify link'),
  });

  const schema = manifest.configSchema?.properties || {};
  const sections = manifest.configSections;
  const isSeo = pluginId === 'seo-copilot';

  // SEO Co-Pilot AI credits (Pro+) — drives the ✨ Research button state.
  const { data: creditsData } = useQuery({
    queryKey: ['seo-ai-credits', projectId],
    queryFn: () => apiHelpers.seoCopilotAiCredits(projectId),
    enabled: isSeo,
  });
  const credits = (creditsData as any)?.data?.data as
    | { allowed: boolean; used: number; limit: number; remaining: number; reason?: string }
    | undefined;

  const researchMutation = useMutation({
    mutationFn: () => apiHelpers.seoCopilotResearchAudience(projectId),
    onSuccess: (res) => {
      const d = (res as any)?.data?.data;
      if (d?.suggestion) {
        setFormState((prev) => ({ ...prev, targetAudience: d.suggestion }));
        toast.success(
          `Drafted your audience — review & Save. ${d.creditsRemaining} AI credit${d.creditsRemaining === 1 ? '' : 's'} left this month.`,
          { duration: 7000 }
        );
      }
      queryClient.invalidateQueries({ queryKey: ['seo-ai-credits', projectId] });
    },
    onError: (err: any) => {
      const e = err?.response?.data?.error;
      toast.error(e?.message || 'AI research failed', { duration: 8000 });
    },
  });

  /**
   * Recursive field renderer. `value`/`onChange` are scoped to the field so
   * nested objects (e.g. blog autoMode / autoSuggest.sources) render real
   * controls instead of the previous "[object Object]" text input.
   */
  const renderSchemaField = (
    key: string,
    field: any,
    value: any,
    onChange: (v: any) => void,
    depth = 0
  ): React.ReactNode => {
    if (!field) return null;
    const title = field.title || key;
    const description = field.description;

    if (field.type === 'object' && field.properties) {
      const obj = value && typeof value === 'object' ? value : {};
      return (
        <div key={key} className={depth === 0 ? 'rounded-lg border p-4' : 'mt-3'}>
          <div className="text-sm font-medium">{title}</div>
          {description && <p className="text-muted-foreground mb-3 text-xs">{description}</p>}
          <div className={depth === 0 ? 'space-y-3' : 'space-y-3 border-l pl-4'}>
            {Object.keys(field.properties).map((subKey) =>
              renderSchemaField(
                subKey,
                field.properties[subKey],
                // Unconfigured sub-field → show the schema default so the UI
                // reflects what the backend will actually do.
                obj[subKey] ?? field.properties[subKey]?.default,
                (v) => onChange({ ...obj, [subKey]: v }),
                depth + 1
              )
            )}
          </div>
        </div>
      );
    }

    if (field.type === 'boolean') {
      return (
        <label
          key={key}
          className={`flex items-center justify-between ${depth === 0 ? 'rounded-lg border p-4' : 'py-1'}`}
        >
          <div>
            <div className="text-sm font-medium">{title}</div>
            {description && <div className="text-muted-foreground text-xs">{description}</div>}
          </div>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded"
          />
        </label>
      );
    }

    if (field.type === 'string' && field.enum) {
      // Options listed in `paidOnlyValues` are selectable only on a paid
      // plugin plan. On free we disable them and show the manifest's
      // `paidOnlyNote` so the admin knows exactly why + how to unlock.
      const onFreePlan = (formState.plan ?? 'free') === 'free';
      const gated = (opt: string) =>
        onFreePlan && Array.isArray(field.paidOnlyValues) && field.paidOnlyValues.includes(opt);
      const showPaidNote = onFreePlan && field.paidOnlyNote && field.paidOnlyValues?.length > 0;
      return (
        <div key={key} className={depth === 0 ? 'rounded-lg border p-4' : ''}>
          <label className="mb-1 block text-sm font-medium">{title}</label>
          {description && <p className="text-muted-foreground mb-2 text-xs">{description}</p>}
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
          >
            {field.enum.map((opt: string, i: number) => (
              <option key={opt} value={opt} disabled={gated(opt)}>
                {(field.enumLabels?.[i] || opt) + (gated(opt) ? ' — paid plans' : '')}
              </option>
            ))}
          </select>
          {showPaidNote && (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              {field.paidOnlyNote}
            </p>
          )}
        </div>
      );
    }

    if (field.type === 'number') {
      return (
        <div key={key} className={depth === 0 ? 'rounded-lg border p-4' : ''}>
          <label className="mb-1 block text-sm font-medium">{title}</label>
          {description && <p className="text-muted-foreground mb-2 text-xs">{description}</p>}
          <input
            type="number"
            value={value ?? ''}
            min={field.minimum}
            max={field.maximum}
            onChange={(e) => onChange(Number(e.target.value))}
            className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
          />
        </div>
      );
    }

    if (field.type === 'array' && Array.isArray(field.items?.enum)) {
      // Closed-vocabulary array → checkbox group. Free-text rows here let
      // typos/case variants into config that strict server-side gates then
      // silently reject (the Thridify pageTypes incident).
      // Map legacy free-text values onto their canonical enum option
      // (case/whitespace/singular-plural variants) so existing config shows
      // checked — and a plain re-save silently repairs the stored array.
      const enumOpts: string[] = field.items.enum;
      const canonical = (v: unknown): string | null => {
        const raw = String(v).trim();
        if (enumOpts.includes(raw)) return raw;
        const lower = raw.toLowerCase();
        return (
          enumOpts.find((o) => {
            const ol = o.toLowerCase();
            return ol === lower || `${ol}s` === lower || ol === `${lower}s`;
          }) ?? null
        );
      };
      const selected = new Set(
        (Array.isArray(value) ? value : []).map(canonical).filter(Boolean) as string[]
      );
      const toggle = (opt: string, on: boolean) => {
        const next = new Set(selected);
        if (on) next.add(opt);
        else next.delete(opt);
        onChange(enumOpts.filter((o) => next.has(o)));
      };
      return (
        <div key={key} className={depth === 0 ? 'rounded-lg border p-4' : ''}>
          <label className="mb-1 block text-sm font-medium">{title}</label>
          {description && <p className="text-muted-foreground mb-2 text-xs">{description}</p>}
          <div className="space-y-1.5">
            {field.items.enum.map((opt: string) => (
              <label key={opt} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.has(opt)}
                  onChange={(e) => toggle(opt, e.target.checked)}
                  className="rounded"
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'array') {
      const items = Array.isArray(value) ? value : [];
      return (
        <div key={key} className={depth === 0 ? 'rounded-lg border p-4' : ''}>
          <label className="mb-1 block text-sm font-medium">{title}</label>
          {description && <p className="text-muted-foreground mb-2 text-xs">{description}</p>}
          <div className="space-y-2">
            {items.map((item: string, i: number) => (
              <div key={i} className="flex gap-2">
                <input
                  value={item}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[i] = e.target.value;
                    onChange(newItems);
                  }}
                  className="bg-background flex-1 rounded-lg border px-3 py-1.5 text-sm"
                />
                <button
                  onClick={() => onChange(items.filter((_: any, idx: number) => idx !== i))}
                  className="text-sm text-red-500 hover:underline"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={() => onChange([...items, ''])}
              className="text-primary text-sm hover:underline"
            >
              + Add item
            </button>
          </div>
        </div>
      );
    }

    // SEO Co-Pilot targetAudience: textarea + ✨ AI Research button (Pro+).
    const isAudienceField = isSeo && key === 'targetAudience' && depth === 0;

    // Default: text input (textarea for the audience field — it's longer).
    return (
      <div key={key} className={depth === 0 ? 'rounded-lg border p-4' : ''}>
        <div className="mb-1 flex items-center justify-between gap-2">
          <label className="block text-sm font-medium">{title}</label>
          {isAudienceField && <AudienceAiButton />}
        </div>
        {description && <p className="text-muted-foreground mb-2 text-xs">{description}</p>}
        {isAudienceField ? (
          <textarea
            rows={3}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
          />
        ) : (
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
          />
        )}
      </div>
    );
  };

  const renderField = (key: string) =>
    renderSchemaField(key, schema[key], formState[key], (v) =>
      setFormState((prev) => ({ ...prev, [key]: v }))
    );

  /**
   * The ✨ "Research with AI" affordance shown beside the target-audience
   * field. Pro+ with credits → active button (spends 1 credit, fills the
   * field with the suggestion for review). Pro+ out of credits → disabled
   * w/ "0 left". Free/Starter (not entitled) → an upgrade hint instead.
   */
  function AudienceAiButton() {
    // Not entitled (free/starter): limit === 0 → show upgrade chip, no spend.
    if (credits && credits.limit === 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
          <Sparkles className="h-3 w-3" /> AI research — Pro
        </span>
      );
    }
    const remaining = credits?.remaining ?? 0;
    const disabled = researchMutation.isPending || remaining <= 0;
    return (
      <button
        type="button"
        onClick={() => researchMutation.mutate()}
        disabled={disabled}
        title={
          remaining > 0
            ? `Draft your target audience with AI (${remaining} credit${remaining === 1 ? '' : 's'} left this month)`
            : 'No AI credits left this month — resets on the 1st'
        }
        className="border-primary text-primary hover:bg-primary hover:text-primary-foreground inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50"
      >
        {researchMutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        Research with AI
        {credits ? ` · ${remaining}/${credits.limit}` : ''}
      </button>
    );
  }

  // Show "Sync from Brand Kit" for plugins that have appearance fields
  const hasAppearanceFields = ['primaryColor', 'brandName', 'welcomeMessage'].some(
    (k) => k in schema
  );

  return (
    <div className="space-y-6">
      {pluginId === 'thridify' && <ThridifyConnectionPanel projectId={projectId} />}

      {pluginId === 'thridify' && (
        <div className="border-primary/30 from-primary/5 flex items-center justify-between rounded-xl border bg-gradient-to-br to-transparent p-4">
          <div>
            <h4 className="text-sm font-medium">Refresh Thridify link</h4>
            <p className="text-muted-foreground text-xs">
              Re-syncs your linked Thridify account: refreshes the account connection, updates the
              domain allowlist (run after adding a custom domain), and re-pushes your product
              catalog so 3D experiences resolve for every product. Safe to press repeatedly.
            </p>
          </div>
          <button
            onClick={() => thridifyResyncMutation.mutate()}
            disabled={thridifyResyncMutation.isPending}
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
          >
            {thridifyResyncMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
            Resync
          </button>
        </div>
      )}

      {pluginId === 'thridify' && (
        <>
          <ThridifyEnvironmentPanel projectId={projectId} />
          <ThridifySwitcherPanel projectId={projectId} />
          <ThridifyPlacementPreview formState={formState} />
        </>
      )}

      {pluginId === 'seo-copilot' && (
        <div className="border-primary/30 from-primary/5 flex items-center justify-between rounded-xl border bg-gradient-to-br to-transparent p-4">
          <div>
            <h4 className="text-sm font-medium">Import from your site &amp; project data</h4>
            <p className="text-muted-foreground text-xs">
              Auto-fill business type, address, phone, and a starting audience from your live
              storefront and project settings. Only fills empty fields — never overwrites what
              you&apos;ve typed.
            </p>
          </div>
          <button
            onClick={() => resyncMutation.mutate()}
            disabled={resyncMutation.isPending}
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
          >
            {resyncMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
            Import data
          </button>
        </div>
      )}

      {hasAppearanceFields && (
        <div className="bg-muted/50 flex items-center justify-between rounded-xl border border-dashed p-4">
          <div>
            <h4 className="text-sm font-medium">Sync theme from Brand Kit</h4>
            <p className="text-muted-foreground text-xs">
              Reset brand name, welcome message, and primary color to match your project's brand
              kit.
            </p>
          </div>
          <button
            onClick={() => syncBrandKitMutation.mutate()}
            disabled={syncBrandKitMutation.isPending}
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
          >
            {syncBrandKitMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Palette className="h-4 w-4" />
            )}
            Sync from Brand Kit
          </button>
        </div>
      )}

      {sections ? (
        sections.map((section) => (
          <div key={section.id} className="bg-card rounded-xl border p-6">
            <h3 className="mb-1 font-semibold">{section.title}</h3>
            {section.description && (
              <p className="text-muted-foreground mb-4 text-sm">{section.description}</p>
            )}
            <div className="space-y-3">
              {section.fields.map((fieldKey) => renderField(fieldKey))}
            </div>
          </div>
        ))
      ) : (
        <div className="bg-card rounded-xl border p-6">
          <h3 className="mb-4 font-semibold">Settings</h3>
          <div className="space-y-3">{Object.keys(schema).map((key) => renderField(key))}</div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-medium transition-colors"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save Settings
        </button>
      </div>
    </div>
  );
}
